/**
 * TanStack Query hooks for PAI API
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

// ============================================================================
// Types
// ============================================================================

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp: Date | number
  id?: string
}

export interface ChatRequest {
  personaId: string
  sessionId: string
  message: string
  context?: {
    viewerProfile?: string
    rolePersona?: string
  }
}

export interface ChatResponse {
  response: string
  model: string
  tokenCount?: number
  latencyMs: number
}

export interface FeedbackRequest {
  personaId: string
  interactionId: string
  sessionId: string
  signalType: 'thumbs_up' | 'thumbs_down' | 'correction' | 'clarification'
  signalValue: any
  userContext?: {
    viewerProfile?: string
    rolePersona?: string
  }
}

// ============================================================================
// API Client Functions
// ============================================================================

async function sendChatMessage(request: ChatRequest): Promise<ChatResponse> {
  const response = await fetch('/api/pai/chat', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to send message' })) as { error?: string }
    throw new Error(error.error || `HTTP ${response.status}`)
  }

  return response.json()
}

async function getConversationHistory(
  personaId: string,
  sessionId: string,
  limit: number = 50
): Promise<{ history: ChatMessage[] }> {
  const url = new URL('/api/pai/history/' + sessionId, window.location.origin)
  url.searchParams.set('personaId', personaId)
  url.searchParams.set('limit', limit.toString())

  const response = await fetch(url.toString())

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch history' })) as { error?: string }
    throw new Error(error.error || `HTTP ${response.status}`)
  }

  return response.json()
}

async function submitFeedback(request: FeedbackRequest): Promise<{ success: boolean }> {
  const response = await fetch('/api/pai/feedback', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to submit feedback' })) as { error?: string }
    throw new Error(error.error || `HTTP ${response.status}`)
  }

  return response.json()
}

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Send a chat message to PAI
 */
export function usePAIChat() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: sendChatMessage,
    onSuccess: (_data, variables) => {
      // Invalidate conversation history to trigger refresh
      queryClient.invalidateQueries({
        queryKey: ['pai-history', variables.personaId, variables.sessionId],
      })
    },
  })
}

/**
 * Get conversation history for a session
 */
export function usePAIHistory(personaId: string, sessionId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['pai-history', personaId, sessionId],
    queryFn: () => getConversationHistory(personaId, sessionId),
    enabled: enabled && !!personaId && !!sessionId,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnMount: true,
  })
}

/**
 * Submit feedback on an AI response
 */
export function usePAIFeedback() {
  return useMutation({
    mutationFn: submitFeedback,
  })
}

// ============================================================================
// Utilities
// ============================================================================

/**
 * Generate a session ID for chat
 * Uses localStorage to persist across page reloads
 */
export function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return 'server-session'

  const STORAGE_KEY = 'pai-session-id'
  let sessionId = localStorage.getItem(STORAGE_KEY)

  if (!sessionId) {
    sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    localStorage.setItem(STORAGE_KEY, sessionId)
  }

  return sessionId
}

/**
 * Clear session ID (force new conversation)
 */
export function clearSessionId(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem('pai-session-id')
}
